import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Button, Drawer, EmptyState, Inbox01, Table } from "@opendate-app/setlist";

import { errorText, toastError } from "../../../shared/toastHelper";

const styles = {
  link: { color: "var(--sl-text-brand)", textDecoration: "none" },
  muted: { color: "var(--sl-text-tertiary)" },
  updated: { fontSize: 12, color: "var(--sl-text-tertiary)" },
  message: { minWidth: 220, maxWidth: 360, whiteSpace: "pre-wrap", color: "var(--sl-text-secondary)" },
  footer: { display: "flex", justifyContent: "center", marginTop: 16 },
};

const formatReceived = (value) => new Date(value).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });

const formatEventDate = (value) => {
  const [year, month, day] = value.split("-").map(Number);
  return new Date(year, month - 1, day).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

const blank = <span style={styles.muted}>—</span>;

const answer = (key, format = (value) => value) => (inquiry) => {
  const value = inquiry.answers?.[key];
  return value === undefined || value === null || value === "" ? blank : format(value);
};

const InquiriesDrawer = ({ page, fields = [], basePath, onClose }) => {
  const [inquiries, setInquiries] = useState([]);
  const [nextPage, setNextPage] = useState(null);
  const [loading, setLoading] = useState(false);

  const request = useRef(null);

  const load = async (pageNumber) => {
    request.current?.abort();
    const controller = new AbortController();
    request.current = controller;
    setLoading(true);
    try {
      const { data } = await axios.get(`${basePath}/${page.id}/inquiries.json`, { params: { page: pageNumber }, signal: controller.signal });
      if (controller.signal.aborted) return;
      setInquiries((previous) => (pageNumber === 1 ? data.inquiries : [...previous, ...data.inquiries]));
      setNextPage(data.next_page);
    } catch (error) {
      if (!controller.signal.aborted) toastError(errorText(error, "Could not load inquiries, please try again."));
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  };

  useEffect(() => {
    setInquiries([]);
    setNextPage(null);
    setLoading(false);
    if (page) load(1);
    return () => request.current?.abort();
  }, [page?.id]);

  const asksFor = (key) => fields.some((field) => field.key === key);

  const columns = [
    {
      key: "received",
      header: "Received",
      render: (inquiry) => (
        <>
          <div>{formatReceived(inquiry.created_at)}</div>
          {inquiry.updated_at !== inquiry.created_at && <div style={styles.updated}>{`Updated ${formatReceived(inquiry.updated_at)}`}</div>}
        </>
      ),
    },
    { key: "name", header: "Name", primary: true, render: (inquiry) => inquiry.name || blank },
    { key: "email", header: "Email", render: (inquiry) => <a style={styles.link} href={`mailto:${inquiry.email}`}>{inquiry.email}</a> },
    { key: "phone", header: "Phone", render: (inquiry) => (inquiry.phone ? <a style={styles.link} href={`tel:${inquiry.phone}`}>{inquiry.phone}</a> : blank) },
    ...(asksFor("company") ? [{ key: "company", header: "Company", render: answer("company") }] : []),
    { key: "event_type", header: "Event type", render: answer("event_type") },
    { key: "event_date", header: "Date", render: answer("event_date", formatEventDate) },
    { key: "guest_count", header: "Guests", align: "right", render: answer("guest_count", (value) => Number(value).toLocaleString()) },
    { key: "budget", header: "Budget", render: answer("budget") },
    { key: "message", header: "Message", render: answer("message", (value) => <div style={styles.message}>{value}</div>) },
  ];

  return (
    <Drawer
      open={Boolean(page)}
      onClose={onClose}
      side="right"
      size="xl"
      backdrop
      title={page ? `Inquiries · ${page.name}` : "Inquiries"}
      subtitle="Most recently updated first. Reply by email or phone to follow up."
    >
      {page && (
        <>
          <Table
            columns={columns}
            data={inquiries}
            getRowKey={(inquiry) => inquiry.id}
            loading={loading && inquiries.length === 0}
            empty={(
              <EmptyState
                icon={Inbox01}
                size="sm"
                title="No inquiries yet"
                description="When someone fills out this page, their inquiry shows up here."
              />
            )}
          />
          {nextPage && (
            <div style={styles.footer}>
              <Button variant="secondary" disabled={loading} onClick={() => load(nextPage)}>Load more</Button>
            </div>
          )}
        </>
      )}
    </Drawer>
  );
};

export default InquiriesDrawer;
